import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { TenantService } from '../services/tenant-service';

/**
 * Route guard that ensures a valid app context exists before allowing access to routes.
 * Public routes (error pages, auth, landing page) are accessible without app context.
 */
export const appContextGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean> => {
  const tenantService = inject(TenantService);
  const router = inject(Router);

  // Check if this is a public route that doesn't require app context
  if (isPublicRoute(route)) {
    return true;
  }

  // Check if app context is initialized
  if (!tenantService.hasValidContext()) {
    // Redirect to error page (which will handle redirect to login if needed)
    await router.navigate(['/error'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  return true;
};

/**
 * Checks if the route is a public route that doesn't require app context
 */
function isPublicRoute(route: ActivatedRouteSnapshot): boolean {
  // Check the route path and its parents
  let current: ActivatedRouteSnapshot | null = route;

  while (current) {
    const path = current.routeConfig?.path;

    if (path) {
      // Public routes that don't require app context
      const publicPaths = ['error', 'not-found', 'server-error', 'auth'];

      // Check if the path matches a public route or starts with a public path
      if (publicPaths.includes(path) || path.startsWith('auth/') || path === '**') {
        return true;
      }
    }

    current = current.parent;
  }

  // Also check the full URL path
  const fullPath = route.pathFromRoot
    .map((r) => r.routeConfig?.path)
    .filter(Boolean)
    .join('/');

  if (fullPath.startsWith('auth/') || fullPath.startsWith('error')) {
    return true;
  }

  return false;
}

/**
 * Higher-order function to create a guard with custom public routes
 */
export function createAppContextGuard(options?: {
  additionalPublicRoutes?: string[];
}): CanActivateFn {
  return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const tenantService = inject(TenantService);
    const router = inject(Router);

    if (
      isPublicRoute(route) ||
      options?.additionalPublicRoutes?.some((p) => state.url.startsWith(p))
    ) {
      return true;
    }

    if (!tenantService.hasValidContext()) {
      await router.navigate(['/error'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    return true;
  };
}
